import {Controller} from '@hotwired/stimulus'
import orgNavigation from 'components/ui/AppLink/orgNavigation'

// Open/close state for the server-rendered sidebar (GF-2954).
//
// Persisted under the same localStorage key the former React island used
// (usePersistentState 'side-bar-is-open'), so the preference survives the
// migration and interoperates while old and new render side by side.
// External togglers — the meeting pages and SectionItem — reach this controller
// through components/SideBar/globalControl, which dispatches 'sidebar:toggle'.
const STORAGE_KEY = 'side-bar-is-open'
const CLOSED_CLASS = 'app-side-bar--closed'
const INITIALIZED_CLASS = 'app-side-bar--initialized'

// Matches $gf-breakpoint-xs (styles/variables-breakpoints.scss) — the former
// SectionItem collapsed the sidebar after a nav click on narrow screens.
const NARROW_SCREEN_WIDTH = 576

function readStoredOpen() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
    return typeof stored === 'boolean' ? stored : true
  } catch (e) {
    return true
  }
}

function writeStoredOpen(open) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(open))
  } catch (e) {
    // localStorage unavailable (private mode / quota) — persistence is best-effort.
  }
}

export default class extends Controller {
  connect() {
    // Restore the saved state without treating it like a user-triggered change.
    this.render(readStoredOpen(), {
      persist: false,
      notify: false,
    })

    // Enable transitions only after the initial state has been applied.
    requestAnimationFrame(() => {
      this.element.classList.add(INITIALIZED_CLASS)
    })

    this.handleExternalToggle = () => this.toggle()
    this.handleSmartNavigation = (event) => this.navigate(event)

    window.addEventListener('sidebar:toggle', this.handleExternalToggle)
    this.element.addEventListener('click', this.handleSmartNavigation)
  }

  disconnect() {
    window.removeEventListener('sidebar:toggle', this.handleExternalToggle)
    this.element.removeEventListener('click', this.handleSmartNavigation)
  }

  toggle() {
    this.render(!this.isOpen)
  }

  // Nav-link click handler: on narrow screens a navigation collapses the
  // sidebar so the destination page is visible (former SectionItem behaviour).
  collapseOnNarrow() {
    if (window.innerWidth < NARROW_SCREEN_WIDTH && this.isOpen)
      this.render(false)
  }

  navigate(event) {
    const link = event.target.closest('a[href]')
    if (!link || !this.element.contains(link))
return

    const href = link.getAttribute('href') || ''
    if (!orgNavigation.shouldUseReactRouter(href))
return

    const targetAttr = link.getAttribute('target') || ''
    const isAuxiliaryClick
      = typeof event.button === 'number' && event.button !== 0
    const isModifiedClick
      = event.metaKey || event.ctrlKey || event.shiftKey || isAuxiliaryClick
    const opensInNewContext
      = targetAttr && targetAttr.toLowerCase() !== '_self'

    if (isModifiedClick || opensInNewContext)
return

    orgNavigation.navigateViaReactRouter(href, () => event.preventDefault())
  }

  get isOpen() {
    return !this.element.classList.contains(CLOSED_CLASS)
  }

  render(open, {persist = true, notify = true} = {}) {
    this.element.classList.toggle(CLOSED_CLASS, !open)
    this.element.setAttribute('data-sidebar-open', open ? 'true' : 'false')

    if (persist)
writeStoredOpen(open)

    // Cross-boundary notification (island-contract rule 3): the OrgSelect
    // island re-reads open state from this event rather than from React props.
    if (notify) {
      window.dispatchEvent(
        new CustomEvent('sidebar:changed', {
          detail: {open},
        }),
      )
    }
  }
}
